import { Alert, Menu, MenuItem, Snackbar } from '@mui/material';
import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  useDeleteApiDeliverablesByIdMutation,
  useGetApiDeliverablesByIdQuery,
  usePostApiDeliverablesByIdCancelMutation,
  usePostApiDeliverablesByIdSubmitMutation,
} from '../../../../../app/athenaApiGen';
import {
  setCreateModal,
  setEditModalOpen,
  setExpandedPanel,
  setViewPanel,
} from '../../../../../app/deliverables/deliverablesSlice';
import {
  useLazyFetchGenericQuery,
  usePostGenericMutation,
} from '../../../../../app/dynamicFormApi';
import { getMissingRequiredFields } from '../../../../../components/dynamic-form/requiredFieldsValidation';
import {
  formRouteByAbbreviation,
  formSchemaByAbbreviation,
  formViewRouteByAbbreviation,
} from '../../../../formSchemaRegistry';
import { RootState } from '../../../../../app/store';
import DeleteConfirmation from '../../../../../components/Modals/DeleteConfirmation';
import NoPermissionTooltip from '../../../../../components/Tooltips/NoPermissionTooltip';
import {
  getDeliverableState,
  getDeliverableType,
  isActionEnabled,
} from '../../../../utilities';

type TActionsMenuProps = {
  anchorEl: null | HTMLElement;
  openMenu: boolean;
  workflowState: string[];
  handleClose: () => void;
};
const ActionsMenu = (props: TActionsMenuProps) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { selectedRowId, allowedActions } = useSelector(
    (state: RootState) => state.deliverables,
  );

  const { data } = useGetApiDeliverablesByIdQuery({
    id: selectedRowId ?? '',
  });
  const deliverableAbbreviation = (
    data?.deliverableType?.abbreviation ?? ''
  ).toLowerCase();
  const isForm = ['ctp', 'pnr', 'rip', 'tar', 'tr'].includes(
    deliverableAbbreviation,
  );
  const [deleteDeliverable, { isLoading: deleting }] =
    useDeleteApiDeliverablesByIdMutation();
  const [submitDeliverable] = usePostApiDeliverablesByIdSubmitMutation();
  const [postGenericDocumentDeliverable] = usePostGenericMutation();
  const [cancelDeliverable] = usePostApiDeliverablesByIdCancelMutation();
  const [fetchFormRecord] = useLazyFetchGenericQuery();

  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  const handleDelete = useCallback(async () => {
    if (selectedRowId) {
      await deleteDeliverable({
        id: selectedRowId.toString(),
      });
      setDeleteDialogOpen(false);
    }

    props.handleClose();
  }, [selectedRowId, deleteDeliverable, props]);

  const handleEdit = useCallback(() => {
    if (selectedRowId) {
      if (!isForm) {
        dispatch(setEditModalOpen(true));
      } else {
        navigate(
          `/${formViewRouteByAbbreviation[deliverableAbbreviation] ?? deliverableAbbreviation}/${selectedRowId}?mode=edit`,
        );
      }
    }

    props.handleClose();
  }, [
    selectedRowId,
    dispatch,
    props,
    isForm,
    navigate,
    deliverableAbbreviation,
  ]);

  // Cancel goes through the dedicated endpoint rather than a raw workflow
  // PATCH to Pending: services 0.29.0 removed the reset-to-Pending transition
  // from the PATCH validator, and the cancel endpoint also resets the tasks,
  // clears DateSubmitted and writes the "Returned" audit rows.
  const handleCancel = useCallback(async () => {
    if (selectedRowId) {
      try {
        await cancelDeliverable({ id: selectedRowId.toString() }).unwrap();
      } catch (error) {
        const data = (
          error as { data?: { details?: string; error?: string } } | undefined
        )?.data;
        setValidationError(
          data?.details || data?.error || 'Failed to cancel. Please try again.',
        );
      }
    }

    props.handleClose();
  }, [selectedRowId, cancelDeliverable, props]);

  // Front-end enforcement of schema requirements before a form is submitted
  // from the table (mirrors the form page's Save & Submit validation). Returns
  // the list of missing required-to-submit field labels, or [] when complete.
  const getSubmitBlockers = useCallback(async (): Promise<string[]> => {
    const schema = formSchemaByAbbreviation[deliverableAbbreviation];
    const route = formRouteByAbbreviation[deliverableAbbreviation];
    if (!schema || !route || !selectedRowId) return [];

    let record: any;
    try {
      record = await fetchFormRecord({
        url: `/api/${route}/${selectedRowId}`,
      }).unwrap();
    } catch {
      // If we can't load the record to validate, don't block on infra errors —
      // the backend remains the final gate.
      return [];
    }

    // rawFormData is a JSON stringify of the exact DynamicForm formData, so it
    // carries every schema field by name. Fall back to the typed record.
    let formData: any = record;
    if (record?.rawFormData) {
      try {
        formData = JSON.parse(record.rawFormData);
      } catch {
        formData = record;
      }
    }

    return getMissingRequiredFields(schema, formData, true);
  }, [deliverableAbbreviation, selectedRowId, fetchFormRecord]);

  const handleSubmit = useCallback(async () => {
    if (selectedRowId) {
      if (!isForm) {
        submitDeliverable({
          id: selectedRowId,
        });
      } else {
        const missing = await getSubmitBlockers();
        if (missing.length > 0) {
          setValidationError(
            `Cannot submit — please complete the following required field${
              missing.length > 1 ? 's' : ''
            }: ${missing.join(', ')}.`,
          );
          props.handleClose();
          return;
        }

        const route = formRouteByAbbreviation[deliverableAbbreviation];
        postGenericDocumentDeliverable({
          url: `/api/${route}/${selectedRowId}/submit`,
          body: data,
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
        });
      }
    }

    props.handleClose();
  }, [
    selectedRowId,
    submitDeliverable,
    props,
    isForm,
    deliverableAbbreviation,
    postGenericDocumentDeliverable,
    data,
    getSubmitBlockers,
  ]);

  const renderMenuItems = useMemo(() => {
    const deliverableType = getDeliverableType(
      data?.deliverableType?.abbreviation ?? '',
    );
    const state = getDeliverableState(data?.workflowState ?? '');

    const archiveDisabled = !isActionEnabled(
      allowedActions,
      `archive-${deliverableType}`,
    );
    const editDisabled = !isActionEnabled(
      allowedActions,
      deliverableType !== 'deliverable'
        ? `update-${deliverableType}`
        : `update-inflight-${deliverableType}`,
    );
    const submitDisabled = !isActionEnabled(
      allowedActions,
      `submit-${deliverableType}`,
    );
    const createDisabled = !isActionEnabled(
      allowedActions,
      `create-${deliverableType}`,
    );
    const viewDisabled = !isActionEnabled(
      allowedActions,
      `get-${state}-${deliverableType}`,
    );
    if (
      data?.workflowState?.includes('Approved') ||
      data?.workflowState?.includes('Rejected')
    ) {
      return [
        <NoPermissionTooltip isDisabled={viewDisabled} key={'completed-view'}>
          <MenuItem
            disabled={viewDisabled}
            onClick={() => {
              dispatch(setViewPanel(true));
              dispatch(setExpandedPanel(true));
              props.handleClose();
            }}
          >
            View
          </MenuItem>
        </NoPermissionTooltip>,
        <NoPermissionTooltip isDisabled={createDisabled} key={'clone'}>
          <MenuItem
            disabled={createDisabled}
            onClick={() => {
              if (isForm && selectedRowId) {
                const cloneRoute =
                  deliverableAbbreviation === 'tar'
                    ? 'tar-imcs'
                    : deliverableAbbreviation;
                navigate(`/${cloneRoute}/${selectedRowId}?mode=clone`);
              } else {
                dispatch(
                  setCreateModal({
                    isOpen: true,
                    cloneDeliverable: data,
                  }),
                );
              }
              props.handleClose();
            }}
          >
            Clone
          </MenuItem>
        </NoPermissionTooltip>,
      ];
    } else if (state === 'inflight') {
      return [
        <NoPermissionTooltip isDisabled={viewDisabled} key={'pending-view'}>
          <MenuItem
            disabled={viewDisabled}
            onClick={() => {
              dispatch(setViewPanel(true));
              dispatch(setExpandedPanel(true));
              props.handleClose();
            }}
          >
            View
          </MenuItem>
        </NoPermissionTooltip>,
        <NoPermissionTooltip isDisabled={editDisabled} key={'pending-edit'}>
          <MenuItem onClick={handleEdit} disabled={editDisabled}>
            Edit
          </MenuItem>
        </NoPermissionTooltip>,
        <NoPermissionTooltip
          isDisabled={archiveDisabled}
          key={'pending-archive'}
        >
          <MenuItem
            onClick={() => {
              setDeleteDialogOpen(true);
              props.handleClose();
            }}
            disabled={archiveDisabled}
          >
            Delete
          </MenuItem>
        </NoPermissionTooltip>,
        <NoPermissionTooltip isDisabled={submitDisabled} key={'pending-submit'}>
          <MenuItem onClick={handleSubmit} disabled={submitDisabled}>
            Submit
          </MenuItem>
        </NoPermissionTooltip>,
      ];
    } else if (state === 'submitted') {
      const actions = [
        <NoPermissionTooltip isDisabled={viewDisabled} key={'submitted-view'}>
          <MenuItem
            disabled={viewDisabled}
            onClick={() => {
              dispatch(setViewPanel(true));
              dispatch(setExpandedPanel(true));
              props.handleClose();
            }}
          >
            View
          </MenuItem>
        </NoPermissionTooltip>,
      ];
      // In "In Internal Review" the deliverable is still recoverable. Non-form
      // deliverables get Edit — their modal returns the workflow to Pending as
      // part of Save (StepperContent). Form pages have no retract path and
      // render read-only past Pending, so form deliverables get Cancel only.
      if (data?.workflowState === 'In Internal Review') {
        if (!isForm) {
          actions.push(
            <NoPermissionTooltip
              isDisabled={editDisabled}
              key={'submitted-edit'}
            >
              <MenuItem onClick={handleEdit} disabled={editDisabled}>
                Edit
              </MenuItem>
            </NoPermissionTooltip>,
          );
        }
        actions.push(
          <NoPermissionTooltip
            isDisabled={editDisabled}
            key={'submitted-cancel'}
          >
            <MenuItem
              disabled={editDisabled}
              onClick={() => {
                handleCancel();
                props.handleClose();
              }}
            >
              Cancel
            </MenuItem>
          </NoPermissionTooltip>,
        );
      }

      return actions;
    } else {
      return [
        <NoPermissionTooltip isDisabled={viewDisabled} key={'view'}>
          <MenuItem
            disabled={viewDisabled}
            onClick={() => {
              dispatch(setViewPanel(true));
              dispatch(setExpandedPanel(true));
              props.handleClose();
            }}
          >
            View
          </MenuItem>
        </NoPermissionTooltip>,
      ];
    }
  }, [
    data?.deliverableId,
    allowedActions,
    isForm,
    handleEdit,
    handleSubmit,
    handleCancel,
    dispatch,
    props,
  ]);

  return (
    <React.Fragment>
      <Menu
        anchorEl={props.anchorEl}
        open={props.openMenu}
        onClose={props.handleClose}
        slotProps={{
          list: {
            'aria-labelledby': 'deliverable-action-button',
          },
        }}
      >
        {renderMenuItems}
      </Menu>

      <DeleteConfirmation
        open={deleteDialogOpen}
        title={`Delete ${data?.deliverableTitle}`}
        onClose={() => setDeleteDialogOpen(false)}
        content={{
          mainText: `Are you sure you want to delete <strong>${data?.deliverableTitle}</strong>? <br /><br /> This action cannot be undone.`,
        }}
        handleConfirmation={handleDelete}
        isLoading={deleting}
      />

      <Snackbar
        open={Boolean(validationError)}
        autoHideDuration={8000}
        onClose={() => setValidationError(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          severity="error"
          onClose={() => setValidationError(null)}
          sx={{ width: '100%' }}
        >
          {validationError}
        </Alert>
      </Snackbar>
    </React.Fragment>
  );
};

export default ActionsMenu;
